import { ChartDataResponse, ChartDataPoint, ChartConfig, NumericDataKey } from '../types';

// UTC時刻をフォーマットするヘルパー関数
export const formatUTCTime = (dateString: string): string => {
  const date = new Date(dateString);
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

// 日付が土日かどうかを判定
export const isWeekend = (dateString: string): boolean => {
  const date = new Date(dateString);
  const day = date.getDay();
  return day === 0 || day === 6; // 0: 日曜日, 6: 土曜日
};

// 日付から日のみを取得
export const getDateOnly = (dateString: string): string => {
  const date = new Date(dateString);
  return date.getDate().toString();
};

// 年月を取得（タイトル用）
export const getYearMonth = (startDate: string, endDate: string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
    return `${start.getFullYear()}年${start.getMonth() + 1}月`;
  }
  
  return `${start.getFullYear()}/${start.getMonth() + 1} - ${end.getFullYear()}/${end.getMonth() + 1}`;
};

// APIレスポンスをチャート用データに変換
export const transformToChartData = (
  apiData: ChartDataResponse,
  dataKey: NumericDataKey
): ChartDataPoint[] => {
  console.log('transformToChartData 開始:', { dataKey, apiData });
  
  if (!apiData || !apiData.dates || !Array.isArray(apiData.dates)) {
    console.error('無効なAPIデータ:', apiData);
    return [];
  }
  
  const dataArray = apiData[dataKey] as (number | null)[];
  
  if (!dataArray || !Array.isArray(dataArray)) {
    console.warn(`データキー ${dataKey} のデータが見つかりません:`, dataArray);
    // データがない場合は日付のみでnull値を返す
    return apiData.dates.map((date) => ({
      date,
      value: null,
      isWeekend: isWeekend(date),
    }));
  }
  
  return apiData.dates.map((date, index) => ({
    date,
    value: dataArray[index] ?? null,
    isWeekend: isWeekend(date),
  }));
};

// チャート設定
export const CHART_CONFIGS: ChartConfig[] = [
  {
    title: '睡眠スコア',
    dataKey: 'sleep_scores',
    unit: '点',
    color: '#3b82f6',
  },
  {
    title: '睡眠効率',
    dataKey: 'sleep_efficiency',
    unit: '%',
    color: '#10b981',
  },
  {
    title: '中途覚醒回数',
    dataKey: 'wake_block_counts',
    unit: '回',
    color: '#f59e0b',
  },
  {
    title: '浅い睡眠割合',
    dataKey: 'light_time_rates',
    unit: '%',
    color: '#6366f1',
  },
  {
    title: 'レム睡眠割合',
    dataKey: 'rem_time_rates',
    unit: '%',
    color: '#8b5cf6',
  },
  {
    title: '深い睡眠割合',
    dataKey: 'deep_time_rates',
    unit: '%',
    color: '#06b6d4',
  },
  {
    title: 'いびき回数',
    dataKey: 'snore_counts',
    unit: '回',
    color: '#ef4444',
  },
  {
    title: 'いびき時間',
    dataKey: 'snore_times',
    unit: '分',
    color: '#dc2626',
  },
  {
    title: '呼吸アラート',
    dataKey: 'apnea_counts',
    unit: '回',
    color: '#f97316',
  },
  {
    title: '寝返り回数',
    dataKey: 'posture_counts',
    unit: '回',
    color: '#7c3aed',
  },
  {
    title: '仰向け時間',
    dataKey: 'supine_times',
    unit: '分',
    color: '#059669',
  },
  {
    title: '横向き時間',
    dataKey: 'sideways_times',
    unit: '分',
    color: '#0d9488',
  },
  {
    title: 'うつ伏せ時間',
    dataKey: 'prone_times',
    unit: '分',
    color: '#0891b2',
  },
  {
    title: '仰向け割合',
    dataKey: 'supine_rates',
    unit: '%',
    color: '#16a34a',
  },
  {
    title: '横向き割合',
    dataKey: 'sideways_rates',
    unit: '%',
    color: '#0f766e',
  },
  {
    title: 'うつ伏せ割合',
    dataKey: 'prone_rates',
    unit: '%',
    color: '#0e7490',
  },
];

// パーセンテージに変換
export const convertToPercentage = (value: number): number => {
  return Math.round(value * 100);
};

// 秒を分に変換
export const convertToMinutes = (value: number): number => {
  return Math.round(value / 60);
};

// 入力値の検証
export const validateUserId = (userId: string): boolean => {
  return /^\d+$/.test(userId) && parseInt(userId) > 0;
};

export const validateDate = (date: string): boolean => {
  return /^\d{4}-\d{2}-\d{2}$/.test(date) && !isNaN(Date.parse(date));
};

export const validateDateRange = (startDate: string, endDate: string): boolean => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return start <= end;
};

// Dateオブジェクトをローカルタイムゾーンでのstring形式（YYYY-MM-DD）に変換
export const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 文字列（YYYY-MM-DD）からローカルタイムゾーンのDateオブジェクトを作成
export const parseLocalDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // monthは0ベース
};

// 日付範囲を生成する関数
export const generateDateRange = (startDate: string, endDate: string): string[] => {
  const dates: string[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  const current = new Date(start);
  while (current <= end) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
};

// データを日付範囲で補完する関数
export const fillMissingDates = (
  data: ChartDataPoint[],
  startDate: string,
  endDate: string
): ChartDataPoint[] => {
  const allDates = generateDateRange(startDate, endDate);
  const dataMap = new Map(data.map(point => [point.date, point]));
  
  return allDates.map(date => {
    const existingData = dataMap.get(date);
    if (existingData) {
      return existingData;
    }
    return {
      date,
      value: null, // データが無い場合はnull
      isWeekend: isWeekend(date),
    };
  });
};